import { pickBy } from 'lodash-es';
import getInstance from 'modules/api/instance';
import { getInstance as getDSInstance } from 'modules/api/digitalServices/instance';
import { setItem } from 'v2/utils/legacy';
import { getFileFromDataURI, formatPhoneNumber } from './utils';

const bbaInstance = getInstance();
const dsInstance = getDSInstance();

export const fetchUserCredits = () => {
  const url = '/user/credits';
  return bbaInstance.get(url);
};

export const fetchUserAddresses = () => {
  const url = '/user/address';
  return bbaInstance.get(url);
};

export const updateDefaultAddress = (_id) => {
  bbaInstance.post(`user/address/${_id}/set-default`);
};

export const upsertUserAddress = ({
  _id,
  firstName,
  lastName,
  addressLine1,
  addressLine2,
  phoneNumber,
  city,
  state,
  country,
  pincode,
  isDefault,
}) => {
  const url = '/user/address';
  const payload = {
    _id,
    firstName,
    lastName,
    addressLine1,
    addressLine2,
    phoneNumber,
    city,
    state,
    country,
    pincode,
    isDefault,
  };

  if (payload.isDefault) {
    bbaInstance.post(`user/address/${payload._id}/set-default`);
  }
  return bbaInstance.post(url, payload);
};

export const deleteUserAddress = ({ addressId }) => {
  const url = '/user/address';
  const params = {
    shipping_address_id: addressId,
  };

  return bbaInstance.delete(url, { params });
};

export const uploadUserImage = ({ type, data, filename, imageFormat }) => {
  const url = '/user/image';
  const params = {
    type,
  };

  const formData = new FormData();

  const file = getFileFromDataURI(data, filename, imageFormat);
  formData.append('user_image', file);

  return bbaInstance.post(url, formData, { params });
};

export const uploadIntakeFormHeadshot = ({ data, filename }) => {
  const url = '/user/image/upload';
  const file = getFileFromDataURI(data, filename);
  const formData = new FormData();
  formData.append('file', file);
  formData.append('type', 'profile');

  return bbaInstance.post(url, formData);
};

export const fetchUserImages = (data) => {
  // to avoid reassigning to variable named 'type'
  const type = data.type === 'book_profile' ? 'profile' : data.type;
  const url = '/user/image';
  const params = {
    type,
  };
  return bbaInstance.get(url, { params });
};

export const updatePreferences = ({ name, email, phone, brokerInfo, headshotId }) => {
  const url = '/user/preferences';
  const params = pickBy(
    {
      name,
      email,
      phone,
      brokerInfo,
      headshotId,
    },
    (value) => Boolean(value),
  );

  return bbaInstance.post(url, params);
};

export const fetchProfile = () => {
  const url = '/user/profile';

  return bbaInstance.get(url);
};

async function updateTemplateDynamicInfo(profileData) {
  try {
    const response = await dsInstance.get('/template-builder');
    const existingData = response.data;

    if (!existingData || Object.keys(existingData).length === 0) {
      // No existing data, skipping update
      return;
    }

    const updatePayload = { ...existingData };

    // Update fullName if firstName or lastName changed
    if (profileData.firstname || profileData.lastname) {
      const firstName = profileData.firstName || existingData.fullName?.split(' ')[0] || '';
      const lastName = profileData.lastName || existingData.fullName?.split(' ').slice(1).join(' ') || '';
      updatePayload.fullName = `${firstName} ${lastName}`.trim();
    }

    // Update phone if changed
    if (profileData.mobilephone) {
      updatePayload.phone = formatPhoneNumber(profileData.mobilephone);
    }

    // Post the updated data
    await dsInstance.post('/template-builder', updatePayload);
  } catch (error) {
    console.error('[updateTemplateDynamicInfo] Failed to update template dynamic info:', error);
  }
}

export const updateProfile = ({ firstName, lastName, phone }) => {
  const url = '/user/profile';

  const params = {
    firstname: firstName,
    lastname: lastName,
    mobilephone: phone,
  };

  updateTemplateDynamicInfo(params);

  return bbaInstance.post(url, params);
};

export const uploadAvatar = ({ avatar }) => {
  const url = '/user/profile';
  const file = getFileFromDataURI(avatar);
  const formData = new FormData();

  formData.append('profile_image', file);

  return bbaInstance.post(url, formData);
};

export const updatePassword = ({ oldPassword, newPassword }) => {
  const url = '/auth/changepassword';

  const params = {
    old_password: oldPassword,
    new_password: newPassword,
  };

  return bbaInstance.post(url, params);
};

export const fetchBookLinks = () => {
  // TODO: needs to be updated when backend endpoint is ready
  const url = '/dashboard';

  return bbaInstance.get(url);
};

export const deleteUserImage = ({ imageId }) => {
  const url = '/user/image';
  const params = {
    image_id: imageId,
  };

  return bbaInstance.delete(url, { params });
};

/**
 * Exchanges a DS auto-login token for a fresh book-builder session.
 *
 * The stored session (`token`, `refreshToken`, `userData`) is replaced only when
 * the exchange really succeeded. Any failure throws and leaves the current
 * session untouched, so a failed re-auth never logs the member out or stores
 * the string 'undefined' as their token.
 */
export const reAuthenticate = async (email, isWJ = false) => {
  const url = 'hubspot/get-auto-login-token';
  const dsResponse = await dsInstance.get(url);
  const autoLoginToken = dsResponse?.data?.token;
  if (!autoLoginToken) {
    throw new Error('reAuthenticate: no auto-login token returned by digital-services');
  }

  const urlAccessToken = 'auth/token';
  const response = await bbaInstance.get(urlAccessToken, {
    params: {
      token: autoLoginToken,
      username: email,
    },
  });

  // The book builder answers HTTP 200 on failure too: the envelope carries
  // `statusCode: 1` and an empty `data`.
  const envelope = response?.data || {};
  const { token, refresh_token: refreshToken, properties } = envelope.data || {};
  if (envelope.statusCode !== 0 || !token) {
    throw new Error(`reAuthenticate: token exchange failed (statusCode=${envelope.statusCode})`);
  }

  setItem('token', token);
  setItem('refreshToken', refreshToken);
  setItem('userData', properties);
  return {
    userData: properties,
    token,
    refreshToken,
  };
};
